import { Fragment } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CheckCircle } from "lucide-react";
import { CreatorAction } from "@/types/types";
import WaitingApprovalAction from "@/components/dashboard_campaigns/waiting-approval-action";
import EmptyState from "@/components/ui/empty-state";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { statusAccentStyle } from "@/constants/statuses";
import { cn } from "@/lib/utils";

interface ActionsListPanelProps {
  filteredActions: CreatorAction[];
  selectedAction: CreatorAction | null;
  onActionClick: (action: CreatorAction) => void;
  loading: boolean;
}

export default function ActionsListPanel({
  filteredActions,
  selectedAction,
  onActionClick,
  loading,
}: ActionsListPanelProps) {
  if (filteredActions.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <EmptyState icon={<CheckCircle />} text="No actions available" />
      </div>
    );
  }

  return (
    <ScrollArea scrollFade className="h-full">
      {filteredActions.map((action: CreatorAction) => {
        const isActive = selectedAction?._id === action?._id;
        return (
          <Fragment key={action?._id}>
            <div
              onClick={() => {
                if (!loading) {
                  onActionClick(action);
                }
              }}
              // The selected row paints in its task type's color — the one
              // defined on its STATUS_MAP entry (statusAccentStyle).
              style={statusAccentStyle(action.actionType)}
              className={cn(
                "px-2.5 pr-3.5 h-14 cursor-pointer transition-all flex items-center justify-between animate-in fade-in-0 duration-250",
                isActive
                  ? "bg-(--status-accent)/5 border-l-4 border-l-(--status-accent) text-(--status-accent)"
                  : "hover:bg-muted/80 transition-none"
              )}
            >
              <div className="flex items-center gap-2">
                <Avatar className="size-10 border">
                  <AvatarImage src={action.avatar} alt={action.username} />
                  <AvatarFallback className="text-sm font-medium">
                    {action.username.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-1 flex-col gap-1 text-sm mr-5">
                  <span className="flex items-center font-medium">
                    @{action.username}
                  </span>
                  <span className="flex items-center opacity-70 text-xs">
                    {action.social?.email}
                  </span>
                </div>
              </div>
              <WaitingApprovalAction
                status={action.actionType}
                _id={action._id}
                isLoading={loading}
                loadingThisCreator={action.actionType}
              />
            </div>
            <Separator />
          </Fragment>
        );
      })}
    </ScrollArea>
  );
}
